/**
 * Drives the "Continue" action on the post-login passkey-setup interstitial
 * (app/Service/Web/Views/auth/passkey-setup.twig).
 *
 * The interstitial enrolls a passkey without a current password: the user
 * just authenticated with their password at login, which opened a short-lived
 * server-side grace window (see WebAuthnService::grantEnrollGrace). So the
 * ceremony POSTs to /v1/webauthn-registration{/options} with no currentPassword
 * and relies on that marker. WebAuthn helpers come from window.WebAuthnHelpers;
 * HTTP goes through the shared ApiClient (JWT-aware). The platform-authenticator
 * support check and reveal/bounce live inline in the Twig template.
 */
(function () {
    'use strict';

    function ready(fn) {
        if (document.readyState !== 'loading') {
            fn();
        } else {
            document.addEventListener('DOMContentLoaded', fn);
        }
    }

    function helpers() {
        return window.WebAuthnHelpers;
    }

    function translate(key, fallback) {
        return helpers() ? helpers().translate('auth', key, fallback) : fallback;
    }

    function suggestedName(defaultName) {
        const h = helpers();
        if (!h) return defaultName;
        const browser = h.detectBrowserLabel(navigator.userAgent);
        const platformHint = navigator.userAgentData?.platform || navigator.platform || '';
        const platform = h.detectPlatformLabel(navigator.userAgent, platformHint, navigator.maxTouchPoints);
        return h.getSuggestedPasskeyName(defaultName, browser, platform);
    }

    function isLocalhostHost() {
        const host = window.location?.hostname || '';
        return host === 'localhost' || host === '127.0.0.1' || host === '::1';
    }

    ready(function () {
        const root = document.getElementById('passkey-setup-root');
        const btn = document.getElementById('passkey-setup-continue');
        if (!root || !btn) return;

        const dest = root.getAttribute('data-dest') || '/app/account';
        const defaultName = root.getAttribute('data-default-name') || 'Passkey';
        const statusEl = document.getElementById('passkey-setup-status');
        const spinner = document.getElementById('passkey-setup-spinner');

        function showError(message) {
            if (!statusEl) return;
            statusEl.textContent = message;
            statusEl.classList.remove('hidden');
        }

        function clearError() {
            if (!statusEl) return;
            statusEl.textContent = '';
            statusEl.classList.add('hidden');
        }

        function setBusy(busy) {
            btn.disabled = busy;
            if (spinner) spinner.classList.toggle('hidden', !busy);
        }

        function proceed() {
            // Clear the busy state before navigating so the card is never left
            // with a disabled button and a spinning indicator if the navigation
            // is intercepted or the page is restored from the back/forward cache.
            setBusy(false);
            window.location.href = dest;
        }

        // The interstitial enrolls only via the fresh-login grace window and
        // never sends a password. A 429 is transient — surface it and let the
        // user wait and retry. Any other rejection means the window has
        // closed (grace expired → 400) or the server refused (→ 403), so
        // retrying without a password cannot succeed. The interstitial is
        // optional, so forward the user to where they were headed instead of
        // stranding them on a button that keeps failing. The prompt stays
        // pending so a capable session is offered again later.
        function handleEnrollFailure(res) {
            if (res && res.status === 429) {
                showError(translate(
                    'passkey_error_rate_limited',
                    'Too many attempts. Wait a moment and try again.'
                ));
                setBusy(false);
                return;
            }
            proceed();
        }

        btn.addEventListener('click', async function () {
            if (!helpers() || !window.ApiClient || !helpers().isWebAuthnSupported()) return;

            clearError();
            setBusy(true);

            // Passkeys require the RP ID (interpals.net) to be a registrable
            // suffix of the page's domain. localhost is not, so the ceremony
            // throws a SecurityError before any OS prompt. Mirror the sign-in
            // button: point the developer at the Cloudflare tunnel URL
            // (https://local-<port>.interpals.net) where dev passkeys work,
            // instead of the misleading "device may already be set up" error.
            if (isLocalhostHost()) {
                showError(translate(
                    'passkey_error_localhost',
                    'Passkeys cannot be used from localhost. Open the Cloudflare tunnel URL for this branch and try again.'
                ));
                setBusy(false);
                return;
            }

            try {
                const optionsRes = await window.ApiClient.request('/webauthn-registration/options', {
                    method: 'POST',
                    body: new FormData(),
                });
                if (!optionsRes.ok) {
                    handleEnrollFailure(optionsRes);
                    return;
                }
                const options = await optionsRes.json();
                const decoded = helpers().decodeCreateOptions(options.publicKey);

                let credential;
                try {
                    credential = await navigator.credentials.create({ publicKey: decoded });
                } catch (e) {
                    if (e && (e.name === 'NotAllowedError' || e.name === 'AbortError')) {
                        // User dismissed the platform prompt. Let them retry
                        // or skip; the grace window is still open.
                        setBusy(false);
                        return;
                    }
                    // Any other create() error is deterministic for this
                    // device/origin (e.g. no matching authenticator, RP-ID
                    // mismatch), so retrying the same button keeps failing.
                    // Forward to the destination instead of stranding the user.
                    if (window.console) console.warn('passkey-setup:', e.name, e.message);
                    proceed();
                    return;
                }

                if (!credential) {
                    setBusy(false);
                    return;
                }

                const formData = new FormData();
                formData.append('challengeId', options.challengeId);
                formData.append('clientDataJSON', helpers().base64UrlEncode(credential.response.clientDataJSON));
                formData.append('attestationObject', helpers().base64UrlEncode(credential.response.attestationObject));
                formData.append('name', suggestedName(defaultName));

                const verifyRes = await window.ApiClient.request('/webauthn-registration', {
                    method: 'POST',
                    body: formData,
                });
                if (!verifyRes.ok) {
                    handleEnrollFailure(verifyRes);
                    return;
                }

                proceed();
            } catch (e) {
                // Transient (e.g. network) failure: keep the user here so they
                // can retry or skip.
                showError(translate(
                    'passkey_setup_error_generic',
                    'Could not add a passkey. This device may already be set up.'
                ));
                if (window.console) console.warn('passkey-setup:', e);
                setBusy(false);
            }
        });

        // Handler attached: enable the button (server renders it disabled so an
        // early click before hydration is not a silent no-op).
        btn.disabled = false;
    });
})();
